import {
  CalendarDays,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  List,
} from "lucide-react";
import {
  Button,
  Text,
  ToggleGroup,
  ToggleGroupItem,
} from "@musa.tech/design-system";
import type { ColetasView } from "@/utils/coletasCalendar";

const ITEM_CLASS =
  "rounded-sm px-3 text-neutral-600 data-[state=on]:bg-white data-[state=on]:text-primary-700 data-[state=on]:shadow-sm";

interface ColetasViewToggleProps {
  view: ColetasView;
  onViewChange: (view: ColetasView) => void;
  periodLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** Falso desabilita o botão "anterior" (não navega para o passado). */
  canPrev?: boolean;
  /** Falso desabilita o botão "próximo" (trava dentro do mês atual). */
  canNext?: boolean;
}

export const ColetasViewToggle = ({
  view,
  onViewChange,
  periodLabel,
  onPrev,
  onNext,
  onToday,
  canPrev = true,
  canNext = true,
}: ColetasViewToggleProps) => {
  // Botões de navegação (‹ Hoje ›) só aparecem na visão Semana.
  // O rótulo do período aparece em Semana e Mês.
  const showNavButtons = view === "semana";
  const showPeriodLabel = view === "semana" || view === "mes";

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {showPeriodLabel ? (
        <div className="flex items-center gap-2">
          {showNavButtons && (
            <>
              <Button
                variant="outline"
                size="icon"
                onClick={onPrev}
                disabled={!canPrev}
                aria-label="Período anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={onToday}>
                Hoje
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={onNext}
                disabled={!canNext}
                aria-label="Próximo período"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
          <Text variant="label" className={showNavButtons ? "ml-2 text-neutral-700" : "text-neutral-700"}>
            {periodLabel}
          </Text>
        </div>
      ) : (
        <div />
      )}

      <ToggleGroup
        type="single"
        value={view}
        onValueChange={(value) => value && onViewChange(value as ColetasView)}
        className="gap-1 rounded-md border border-border bg-neutral-100 p-1"
      >
        <ToggleGroupItem
          value="agenda"
          aria-label="Visão agenda"
          className={ITEM_CLASS}
        >
          <List className="mr-1 h-4 w-4" />
          Agenda
        </ToggleGroupItem>
        <ToggleGroupItem
          value="semana"
          aria-label="Visão semana"
          className={ITEM_CLASS}
        >
          <CalendarRange className="mr-1 h-4 w-4" />
          Semana
        </ToggleGroupItem>
        <ToggleGroupItem
          value="mes"
          aria-label="Visão mês"
          className={ITEM_CLASS}
        >
          <CalendarDays className="mr-1 h-4 w-4" />
          Mês
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
};
